import { FC, useState } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { useFiles } from '@/entities/file';
import { SupportedDoc, useIwExecutionStore } from '@/entities/interwills';
import { useNotices } from '@/features/notices/useNotices';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { files } from '@/shared/utils/files';
import { numbers } from '@/shared/utils/numbers';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { DocumentGalleryModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';

export const DocumentGalleryModal: FC<DocumentGalleryModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'documentGallery' });

  const modals = useModalsStore();
  const { supportedDocuments } = useIwExecutionStore();
  const { decryptDocumentFile } = useFiles();
  const notices = useNotices();
  const [loading, setLoading] = useState(false);

  const openFile = async (doc: SupportedDoc) => {
    setLoading(true);
    const file = await decryptDocumentFile(doc.url);

    if (file) {
      modals.closeModal();
      modals.openModal({
        type: 'FilePreviewModal',
        mode: 'fileInfo',
        fileInfo: {
          name: doc.name,
          fileType: 'pdf',
          url: files.blobToUrl(file),
        },
      });
    } else {
      notices.error('Unable to decrypt the file');
    }
    setLoading(false);
  };

  return (
    <ModalContainerV1 title={t('title')}>
      <InnerForm>
        {loading && (
          <LoaderWrapper>
            <Spinner width={76} height={76} src="/assets/icons/loader.svg" alt={t('loading')} />
            {/* <LoaderText>{t('loading')}</LoaderText> */}
          </LoaderWrapper>
        )}
        {!loading &&
          supportedDocuments.map((doc, i) => (
            <DocumentWrapper
              key={i}
              onClick={() => {
                openFile(doc);
              }}
            >
              <IconWrapper>
                <Icon size="s20" name="file-pdf" />
              </IconWrapper>
              <ItemContent>
                <FileTitle>{doc.name}</FileTitle>
                <span>{numbers.formatMemoryFromBytes(doc.size)}</span>
              </ItemContent>
            </DocumentWrapper>
          ))}
      </InnerForm>
      <Buttons>
        <ButtonIw onClick={modals.closeModal}>{t('close')}</ButtonIw>
      </Buttons>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 24px;
  min-height: 240px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
`;

const DocumentWrapper = styled.div`
  display: flex;
  padding: 12px 0;
  gap: 8px;
  align-items: stretch;
  cursor: pointer;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  background: ${COLORS.systemSky100};
  padding: 4px;
  border-radius: 4px;
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
`;

const FileTitle = styled.b`
  font-size: 14px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
  align-items: center;
  height: calc(240px - 24px - 24px);
  justify-content: center;
`;

const Spinner = styled(Image)`
  animation: spin 2s linear infinite;

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`;

// const LoaderText = styled.b`
//   font-size: 18px;
// `;
